import { capitalize } from 'lodash';
import { useEffect, useMemo } from 'react';
import { Drawer, DrawerProps, Form, Space } from 'antd';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from 'components/new-ui/base/button/Button';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';
import FormSelect, { SelectOption } from 'components/new-ui/composed/formSelect/FormSelect';
import { DeclarationCountry, declarationCountryLabels } from 'store/declarations/enums/common/declaration-country';
import { getRequiredMessage } from 'views/declarations/utils/validation-utils';
import {
    DeclarationInternalType,
    irelandInternalDeclarationTypes,
    ukInternalDeclarationTypes,
} from 'store/declarations/enums/common/declaration-internal-type';

export type NewDeclarationDrawerFormValues = {
    declarationType: DeclarationInternalType;
    country: DeclarationCountry;
};

interface Props extends DrawerProps {
    onSelectDeclarationInfo: (values: NewDeclarationDrawerFormValues) => void;
}

// Remove ENS and ArrivalAtExit
const filteredIrelandInternalDeclarationTypes = irelandInternalDeclarationTypes.filter(
    (internalType) => ![DeclarationInternalType.ENS, DeclarationInternalType.ARRIVAL].includes(internalType.id)
);

const validationSchema = Yup.object().shape({
    declarationType: Yup.string().required(getRequiredMessage('declarationType')).nullable(),
    country: Yup.string().required(getRequiredMessage('country')).nullable(),
});

const declarationCountriesSelectOptions: SelectOption[] = declarationCountryLabels.map((item) => ({
    id: item.id,
    value: capitalize(item.value),
}));

// We don't allow creating ENS from the drawer
const filteredDeclarationSelectOptions: SelectOption[] = declarationCountriesSelectOptions.filter(
    (country) => country.id !== DeclarationCountry.EU
);

const NewDeclarationDrawer = ({ onClose, onSelectDeclarationInfo, ...rest }: Props) => {
    const {
        values,
        isValid,
        isSubmitting,
        resetForm,
        handleSubmit,
        submitForm,
        getFieldProps,
        getFieldHelpers,
        getFieldMeta,
        setFieldValue,
        setFieldTouched,
    } = useFormik<NewDeclarationDrawerFormValues>({
        initialValues: {
            country: DeclarationCountry.IRELAND,
            declarationType: DeclarationInternalType.IMPORT,
        },
        validationSchema,
        validateOnMount: true,
        onSubmit: (values) => handleCreateDeclaration(values),
    });

    const handleCreateDeclaration = (values: NewDeclarationDrawerFormValues) => {
        onSelectDeclarationInfo(values);
    };

    const declarationTypeOptions = useMemo(
        () =>
            values.country === DeclarationCountry.IRELAND
                ? filteredIrelandInternalDeclarationTypes
                : ukInternalDeclarationTypes,
        [values.country]
    );

    useEffect(() => {
        if (!declarationTypeOptions.length) return;

        const current = values.declarationType;
        const existsInNewList = declarationTypeOptions.some((o) => o.id === current);

        if (!existsInNewList) {
            setFieldValue('declarationType', declarationTypeOptions[0].id, false);
            setFieldTouched('declarationType', false, false);
        }
    }, [declarationTypeOptions, values.country]);

    const handleClose = (e: React.MouseEvent | React.KeyboardEvent) => {
        onClose?.(e);
        setTimeout(() => {
            resetForm();
        }, 100);
    };

    return (
        <Drawer
            width={400}
            title="New Declaration"
            maskClosable={false}
            onClose={handleClose}
            extra={
                <Space>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button type="primary" onClick={submitForm} disabled={!isValid} loading={isSubmitting}>
                        Create
                    </Button>
                </Space>
            }
            {...rest}
        >
            <form onSubmit={handleSubmit}>
                <Form layout="vertical">
                    <Space direction="vertical" size={24} style={{ width: '100%' }}>
                        <label>
                            <LabelRequiredSymbol style={{ marginRight: 8 }} />
                            Country
                            <FormSelect
                                hideKeys
                                placeholder="Select country"
                                selectOptions={filteredDeclarationSelectOptions}
                                fieldProps={getFieldProps('country')}
                                fieldMeta={getFieldMeta('country')}
                                fieldHelper={getFieldHelpers('country')}
                            />
                        </label>
                        <label>
                            <LabelRequiredSymbol style={{ marginRight: 8 }} />
                            Declaration Type
                            <FormSelect
                                hideKeys
                                placeholder="Select declaration type"
                                selectOptions={declarationTypeOptions}
                                fieldProps={getFieldProps('declarationType')}
                                fieldMeta={getFieldMeta('declarationType')}
                                fieldHelper={getFieldHelpers('declarationType')}
                            />
                        </label>
                    </Space>
                </Form>
            </form>
        </Drawer>
    );
};

export default NewDeclarationDrawer;
